import { z } from "zod";
import { MV_ACTIVITY_TYPES } from "@/db/schema/movement";
import type { GPSPoint } from "./gps-utils";

export const gpsSampleSchema = z.object({
  sampleId: z.string().min(1).max(256).optional(),
  segmentId: z.number().int().min(0).max(100000).optional(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  altitude: z.number().min(-12000).max(100000).optional(),
  accuracy: z.number().min(0).max(100000).optional(),
  speed: z.number().min(0).max(1000).optional(),
  heading: z.number().min(0).max(360).optional(),
  timestamp: z.iso.datetime({ offset: true }),
});

export const movementSyncSchema = z
  .object({
    schemaVersion: z.literal(2).optional(),
    accountId: z.string().min(1).max(256).optional(),
    clientId: z.string().trim().min(1).max(256),
    activityType: z.enum(MV_ACTIVITY_TYPES),
    startedAt: z.iso.datetime({ offset: true }),
    pointStartIndex: z.number().int().min(0).max(10000000).default(0),
    points: z.array(gpsSampleSchema).max(400),
    moduleActivationId: z.number().int().positive().nullish(),
    routeId: z.string().min(1).max(256).nullish(),
  })
  .superRefine((input, ctx) => {
    if (
      input.schemaVersion === 2 &&
      (!input.accountId || input.points.some((p) => !p.sampleId))
    )
      ctx.addIssue({
        code: "custom",
        message: "Version 2 requires account and sample identities",
      });
    const ids = input.points.flatMap((p) => (p.sampleId ? [p.sampleId] : []));
    if (new Set(ids).size !== ids.length)
      ctx.addIssue({ code: "custom", message: "Duplicate sample ID in batch" });
  });

export const movementSaveSchema = z
  .object({
    schemaVersion: z.literal(2).optional(),
    accountId: z.string().min(1).max(256).optional(),
    clientId: z.string().trim().min(1).max(256),
    activityType: z.enum(MV_ACTIVITY_TYPES),
    /** The type the recording was started (and uploaded) as, when the saved
     * type was chosen at the end instead. Identity checks use this one. */
    recordedActivityType: z.enum(MV_ACTIVITY_TYPES).optional(),
    name: z.string().max(200).optional(),
    notes: z.string().max(10000).optional(),
    startedAt: z.iso.datetime({ offset: true }),
    completedAt: z.iso.datetime({ offset: true }),
    points: z.array(gpsSampleSchema).max(200000),
    requiredSampleIds: z
      .array(z.string().min(1).max(256))
      .max(200000)
      .optional(),
    pausedSeconds: z.number().nonnegative().optional(),
    stats: z.object({
      durationSeconds: z.number().nonnegative(),
      movingTimeSeconds: z.number().nonnegative(),
      distanceMeters: z.number().nonnegative(),
      elevationGainMeters: z.number().nonnegative(),
      elevationLossMeters: z.number().nonnegative(),
      avgPaceSecondsPerKm: z.number().nonnegative(),
      avgSpeedMps: z.number().nonnegative(),
      maxSpeedMps: z.number().nonnegative(),
      recordingGapSeconds: z.number().nonnegative().default(0),
      maxRecordingGapSeconds: z.number().nonnegative().default(0),
    }),
    isPublic: z.boolean().optional(),
    contextType: z.string().max(256).nullish(),
    contextId: z
      .union([z.string().max(256), z.number().int().positive()])
      .nullish(),
    moduleActivationId: z
      .union([z.string().regex(/^\d+$/).max(20), z.number().int().positive()])
      .nullish(),
  })
  .superRefine((input, ctx) => {
    if (
      input.schemaVersion === 2 &&
      (!input.accountId || !input.requiredSampleIds)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Version 2 requires account and sample identities",
      });
    }
    const duration =
      (Date.parse(input.completedAt) - Date.parse(input.startedAt)) / 1000;
    if (
      duration < 0 ||
      Date.parse(input.completedAt) > Date.now() + 60000 ||
      (input.pausedSeconds !== undefined && input.pausedSeconds > duration)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid recording timestamps or paused duration",
      });
    }
  });

/** Invalid sensor times are never replaced with the upload time. */
export function normalizeGPSPoint(point: unknown): GPSPoint | null {
  const parsed = gpsSampleSchema.safeParse(point);
  return parsed.success
    ? {
        ...parsed.data,
        timestamp: new Date(parsed.data.timestamp).toISOString(),
      }
    : null;
}

/** Only a contiguous legacy prefix is acknowledged; MAX(index) can hide holes. */
export function contiguousPointCount(indices: number[]): number {
  const present = new Set(indices);
  let next = 0;
  while (present.has(next)) next++;
  return next;
}

/** Match the immutable values persisted in the sync table. Heading is display-only. */
export function sameGPSSample(a: GPSPoint, b: GPSPoint): boolean {
  return (
    a.latitude === b.latitude &&
    a.longitude === b.longitude &&
    Date.parse(a.timestamp) === Date.parse(b.timestamp) &&
    (a.altitude ?? null) === (b.altitude ?? null) &&
    (a.accuracy ?? null) === (b.accuracy ?? null) &&
    (a.speed ?? null) === (b.speed ?? null) &&
    (a.segmentId ?? 0) === (b.segmentId ?? 0)
  );
}

export function mergeGPSStreams(...streams: GPSPoint[][]): GPSPoint[] {
  const samples = new Map<string, GPSPoint>();
  for (const point of streams.flat()) {
    const id =
      point.sampleId ??
      `legacy:${point.timestamp}:${point.latitude}:${point.longitude}`;
    const existing = samples.get(id);
    if (existing && !sameGPSSample(existing, point))
      throw new Error(
        "A GPS sample changed after it was recorded. Its original data has been retained.",
      );
    samples.set(id, point);
  }
  return [...samples.values()].sort(
    (a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp),
  );
}
